import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Calendar, CalendarDays, FileText, LogOut, Menu, X,
  Mail, Search, Users, BarChart3, Settings2, UserCog, Shield, MessageSquare,
  Activity, Building2, IndianRupee, Clock, Tag, Megaphone, Settings, Trophy,
  BookOpen, TrendingUp, TrendingDown, MessagesSquare, Send, Grid3X3, Languages,
  BookOpenCheck, ClipboardList, CalendarX, GraduationCap, Rocket, Medal,
  CalendarRange, SendHorizontal, PhoneCall, Receipt, Wallet, Smartphone, Award,
  School, Gauge, TableProperties, Mic, ShoppingBag, ChevronDown, ChevronRight, Banknote, Tv2,
} from 'lucide-react';

// ─── Group Definitions ──────────────────────────────────────────────────────
// adminOnly: false  → visible to all logged-in staff
// adminOnly: true   → visible to role=admin only

const GROUPS = [
  {
    id: 'bookings',
    label: 'Bookings',
    icon: Calendar,
    adminOnly: false,
    items: [
      { to: '/office/slot-booking',          icon: CalendarRange,  label: 'Slots' },
      { to: '/office/email-bookings',        icon: Mail,           label: 'Conflicts' },
      { to: '/office/bookings',              icon: ClipboardList,  label: 'Booking List' },
      { to: '/office/whatsapp-conversations',icon: MessagesSquare, label: 'Chatbox' },
      { to: '/office/cancellations',         icon: CalendarX,      label: 'Cancellations' },
      { to: '/office/whatsapp-booking',      icon: MessageSquare,  label: 'WA Bookings' },
    ],
  },
  {
    id: 'court-management',
    label: 'Court Management',
    icon: Grid3X3,
    adminOnly: false,
    items: [
      { to: '/office/slot-inventory', icon: Settings2,   label: 'Bulk Blocks' },
      { to: '/office/occupancy',      icon: BarChart3,   label: 'Court Utilisation' },
      { to: '/office/slot-finder',    icon: Search,      label: 'Slot Finder' },
      { to: '/office/calendar',       icon: CalendarDays,label: 'Court Calendar' },
    ],
  },
  {
    id: 'programs-events',
    label: 'Events & Programs',
    icon: Trophy,
    adminOnly: false,
    items: [
      { to: '/office/coaching',       icon: GraduationCap, label: 'Coaching' },
      { to: '/office/events',         icon: Medal,         label: 'Events' },
      { to: '/office/school',         icon: School,        label: 'School' },
      { to: '/office/tournament-hub', icon: Award,         label: 'Tournaments Hub' },
    ],
  },
  {
    id: 'communication',
    label: 'Communication',
    icon: MessagesSquare,
    adminOnly: false,
    items: [
      { to: '/office/whatsapp-conversations', icon: MessagesSquare, label: 'Chatbox' },
      { to: '/office/template-logs',          icon: Send,           label: 'WA Logs' },
      { to: '/office/review-log',             icon: ClipboardList,  label: 'WA Review Logs' },
    ],
  },
  // ── Admin-only groups ──────────────────────────────────────────────────────
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    adminOnly: true,
    items: [
      { to: '/office/mgmt',     icon: Gauge,     label: 'MGMT' },
      { to: '/office/analysis', icon: TrendingUp, label: 'Analysis' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: FileText,
    adminOnly: true,
    items: [
      { to: '/office/dashboard',            icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/office/occupancy',            icon: BarChart3,       label: 'Court Utilisation' },
      { to: '/office/reports',              icon: FileText,        label: 'Revenue' },
      { to: '/office/daily-sales-report',   icon: SendHorizontal,  label: 'DSR' },
      { to: '/office/day-book',             icon: BookOpenCheck,   label: 'Daily Sales' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: IndianRupee,
    adminOnly: true,
    items: [
      { to: '/office/pnl',                  icon: TrendingDown,  label: 'Profit n Loss' },
      { to: '/office/expenses',             icon: Receipt,       label: 'Expenses' },
      { to: '/office/funds',                icon: Wallet,        label: 'MAKS Funds' },
      { to: '/office/day-book',             icon: BookOpenCheck, label: 'Daily Sales' },
    ],
  },
  {
    id: 'players',
    label: 'Players',
    icon: Users,
    adminOnly: true,
    items: [
      { to: '/office/contacts',       icon: Users,      label: 'Contacts' },
      { to: '/office/app-users',      icon: Smartphone, label: 'APP Users' },
      { to: '/office/event-players',  icon: Medal,      label: 'Event Players' },
      { to: '/office/calling-data',   icon: PhoneCall,  label: 'Calling Data' },
      { to: '/office/pos-players',    icon: ShoppingBag, label: 'POS Players' },
    ],
  },
  {
    id: 'venue',
    label: 'Venue',
    icon: Building2,
    adminOnly: true,
    items: [
      { to: '/office/venue-court-master',  icon: Building2,   label: 'Venue & Court Master' },
      { to: '/office/working-hours',       icon: Clock,       label: 'Working Hours' },
      { to: '/office/pricing-dashboard',   icon: IndianRupee, label: 'Pricing' },
      { to: '/office/venue-aliases',       icon: Languages,   label: 'Venue Aliases' },
      { to: '/office/coupons',             icon: Tag,         label: 'Coupons' },
    ],
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: MessagesSquare,
    adminOnly: true,
    items: [
      { to: '/office/whatsapp-conversations', icon: MessagesSquare, label: 'Chatbox' },
      { to: '/office/wa-campaigns',       icon: Send,      label: 'WA Campaign' },
      { to: '/office/marketing',          icon: Rocket,    label: 'Marketing' },
      { to: '/office/whatsapp-analytics', icon: TrendingUp,label: 'WA Analytics' },
      { to: '/office/coupons',            icon: Tag,       label: 'Coupons' },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: Shield,
    adminOnly: true,
    items: [
      { to: '/office/users',          icon: UserCog,    label: 'Users' },
      { to: '/office/login-logs',     icon: Activity,   label: 'Login Activity' },
      { to: '/office/stats',          icon: BarChart3,  label: 'Stats' },
      { to: '/office/announcements',  icon: Megaphone,  label: 'Announcements' },
      { to: '/office/knowledge-base', icon: BookOpen,   label: 'Knowledge Base' },
      { to: '/office/voice-api',      icon: Mic,        label: 'Voice API' },
    ],
  },
];

// Standalone admin items (not grouped)
const ADMIN_SOLO = [
  { to: '/office/duty-roster',    icon: TableProperties, label: 'Duty Roster' },
  { to: '/office/venue-events',   icon: Trophy,          label: 'Venue Bookings' },
  { to: '/office/settings',       icon: Settings,        label: 'Settings' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
/** Returns the set of group IDs whose items include the given path */
function getGroupsForPath(path) {
  const ids = new Set();
  for (const g of GROUPS) {
    if (g.items.some(item => item.to === path)) ids.add(g.id);
  }
  return ids;
}

// ─── Component ───────────────────────────────────────────────────────────────
export const Sidebar = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  // openGroups: Set of group IDs the user has explicitly expanded
  // Starts with the groups that contain the initial active route
  const [openGroups, setOpenGroups] = useState(() => {
    const active = getGroupsForPath(location.pathname);
    const saved = sessionStorage.getItem('sidebar_open_group');
    if (saved && (active.size === 0 || active.has(saved))) return new Set([saved]);
    return active.size ? new Set([[...active][0]]) : new Set();
  });
  useEffect(() => {
    const cur = [...openGroups][0];
    if (cur) sessionStorage.setItem('sidebar_open_group', cur);
    else sessionStorage.removeItem('sidebar_open_group');
  }, [openGroups]);

  const isAdmin = admin?.role === 'admin' || admin?.email === 'admin@maks.com';

  // Accordion: only one group open — keep the open one if it holds the route, else open the route's first group
  useEffect(() => {
    const active = getGroupsForPath(location.pathname);
    if (active.size > 0) {
      setOpenGroups(prev => {
        const cur = [...prev][0];
        if (cur && active.has(cur)) return prev;
        return new Set([[...active][0]]);
      });
    }
  }, [location.pathname]);

  const toggleGroup = useCallback((id) => {
    setOpenGroups(prev => (prev.has(id) ? new Set() : new Set([id])));
    setTimeout(() => {
      document.querySelectorAll(`[data-testid="nav-group-${id}"]`).forEach(el => {
        if (el.offsetParent) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }, 50);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/office/login');
  };

  const closeMobile = () => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Shared link class builders ─────────────────────────────────────────────
  const linkClass = (isActive, isAdminItem = false) => {
    const base = 'flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors duration-150 ';
    if (isActive)   return base + (isAdminItem ? 'bg-red-600 text-white' : 'bg-primary text-white');
    return base + (isAdminItem
      ? 'text-slate-600 hover:bg-red-50 hover:text-red-700'
      : 'text-slate-600 hover:bg-slate-100');
  };

  // ── CollapsibleGroup ──────────────────────────────────────────────────────
  const CollapsibleGroup = ({ group }) => {
    const isOpen = openGroups.has(group.id);
    const hasActive = group.items.some(i => i.to === location.pathname);
    const isAdminItem = group.adminOnly;

    return (
      <div>
        <button
          onClick={() => toggleGroup(group.id)}
          data-testid={`nav-group-${group.id}`}
          className={
            `w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors duration-150 text-left ` +
            (hasActive
              ? (isAdminItem ? 'bg-red-50 text-red-700 font-semibold' : 'bg-primary/10 text-primary font-semibold')
              : (isAdminItem ? 'text-slate-600 hover:bg-red-50 hover:text-red-700' : 'text-slate-600 hover:bg-slate-100'))
          }
        >
          <group.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
          <span className="flex-1 font-medium">{group.label}</span>
          {isOpen
            ? <ChevronDown  className="w-4 h-4 shrink-0 opacity-60" />
            : <ChevronRight className="w-4 h-4 shrink-0 opacity-60" />
          }
        </button>

        {isOpen && (
          <div className={`ml-4 mt-0.5 mb-0.5 space-y-0.5 border-l-2 pl-2 ${isAdminItem ? 'border-red-100' : 'border-slate-200'}`}>
            {group.items.map(item => (
              <NavLink
                key={`${group.id}-${item.to}`}
                to={item.to}
                data-testid={`nav-${group.id}-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={closeMobile}
                className={({ isActive }) => linkClass(isActive, isAdminItem) + ' text-sm'}
              >
                <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── NavContent ────────────────────────────────────────────────────────────
  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="p-5 pb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center text-white font-heading font-bold text-base">
            M
          </div>
          <div>
            <h1 className="font-heading font-bold text-base text-slate-900 tracking-tight">MAKS</h1>
            <p className="text-[11px] text-slate-500">Occupancy Dashboard</p>
          </div>
        </div>
      </div>

      {/* User & Logout */}
      <div className="px-4 pb-3 mb-1 border-b border-slate-200">
        <div className="flex items-center gap-2.5 px-1 py-1.5">
          <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground font-semibold text-xs shrink-0">
            {admin?.name?.[0]?.toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate leading-tight">{admin?.name}</p>
            {isAdmin && <p className="text-[10px] text-red-600 font-medium">Administrator</p>}
            {admin?.role === 'event_admin' && <p className="text-[10px] text-indigo-600 font-medium" data-testid="event-admin-role-label">Event Admin</p>}
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">

        {admin?.role === 'event_admin' && (
          <NavLink
            to="/office/events"
            data-testid="nav-my-events"
            onClick={closeMobile}
            className={({ isActive }) => linkClass(isActive)}
          >
            <Medal className="w-5 h-5 shrink-0" strokeWidth={1.5} />
            <span className="font-medium">My Events</span>
          </NavLink>
        )}
        {admin?.role !== 'event_admin' && (<>
        {/* Dashboard — always visible */}
        <NavLink
          to="/office/dashboard"
          data-testid="nav-dashboard"
          onClick={closeMobile}
          className={({ isActive }) => linkClass(isActive)}
        >
          <LayoutDashboard className="w-5 h-5 shrink-0" strokeWidth={1.5} />
          <span className="font-medium">Dashboard</span>
        </NavLink>

        {/* Daily Sales — visible to all staff */}
        <NavLink
          to="/office/day-book"
          data-testid="nav-daily-sales"
          onClick={closeMobile}
          className={({ isActive }) => linkClass(isActive)}
        >
          <BookOpenCheck className="w-5 h-5 shrink-0" strokeWidth={1.5} />
          <span className="font-medium">Daily Sales</span>
        </NavLink>

        {/* Non-admin collapsible groups */}
        {GROUPS.filter(g => !g.adminOnly).map(g => (
          <CollapsibleGroup key={g.id} group={g} />
        ))}
        </>)}

        {/* ── Admin Section ── */}
        {isAdmin && (
          <>
            <div className="pt-3 pb-1.5 px-3">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                <Shield className="w-3 h-3" /> Admin
              </p>
            </div>

            {/* Admin collapsible groups */}
            {GROUPS.filter(g => g.adminOnly).map(g => (
              <CollapsibleGroup key={g.id} group={g} />
            ))}

            {/* Admin standalone items */}
            {ADMIN_SOLO.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={closeMobile}
                className={({ isActive }) => linkClass(isActive, true)}
              >
                <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg text-slate-700 hover:bg-slate-50 transition-colors"
        data-testid="mobile-menu-button"
      >
        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-white border-r border-slate-200 noise-bg h-full">
        <NavContent />
      </aside>

      {/* Mobile overlay + drawer */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 flex flex-col noise-bg">
            <NavContent />
          </aside>
        </>
      )}
    </>
  );
};
